import React, { useState } from "react";
import {
  Box,
  makeStyles,
  Typography,
  IconButton,
  Checkbox,
  Divider
} from "@material-ui/core";
import { checkIsFOJW, downloadStops, makeTextPlural } from "../../utils/helpers";
import { setInfoDialog } from "../../store/actions/common";
import { connect } from "react-redux";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import RestoreIcon from "@material-ui/icons/Restore";
import CameraIcon from "@material-ui/icons/CameraAlt";
import { ReactComponent as NoPhotographyIcon } from "../../assets/images/icons/no-photography.svg";

import OptimizedTooltip from "../common/OptimizedTooltip";
import {
  deleteStops,
  reopenStops,
  setPodPhotoRequirementForStops
} from "../../store/actions/custom";
import { setLoadingRoutesList } from "../../store/actions/loading";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import DeleteIcon from "@material-ui/icons/Delete";
import SmallSearchBox from "../common/SmallSearchBox";
import MoveToVehicle from "../extendablePanel/blocks/MoveToVehicle";
import { setLoadingStop } from "../../store/actions/loading";

const useStyles = makeStyles(theme => ({
  toolbarWrapper: {
    height: 40,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 2, 2, 3),

    "& > div": {
      display: "flex",
      alignItems: "center"
    }
  },
  button: {
    marginLeft: theme.spacing(1.5)
  },
  iconButton: {
    "&.Mui-disabled": {
      opacity: 0.5
    }
  },
  searchBox: {
    height: 40,
    transition: "width 0.3s",
    marginLeft: 0,
    width: "100%"
  },
  toolbarActions: {
    boxSizing: "border-box",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    borderWidth: "1px 0",
    borderStyle: "solid",
    borderColor: "#EAEBF0",
    filter: "drop-shadow(0px 0px 12px rgba(0, 0, 0, 0.16))"
  },
  checkboxWrapper: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(-1),
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main
    }
  },
  selectedText: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontSize: "0.875rem",
    lineHeight: "1rem"
  }
}));

function RoutesToolbar(props) {
  const {
    backhaulGroup,
    published,
    selectedStops,
    handleSelectAllClick,
    allStops,
    locked,
    deleteStops,
    searchInputValue,
    setSearchInputValue,
    isUnassigned,
    routeGroups,
    showingRouteId,
    onSearchFocus,
    onSearchBlur,
    isPastProject
  } = props;

  // General
  const classes = useStyles();

  // States
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [reopenConfirmation, setReopenConfirmation] = useState(false);

  // Variables
  const numSelected = selectedStops.length;
  const allStopsSelected = numSelected === allStops.length;
  const hasCompletedStopInSelection = allStops
    .filter(stop => selectedStops.includes(stop.id))
    .some(stop => stop.status === "finished" || stop.status === "skipped");

  const isDifferentStops =
    backhaulGroup.length > 1
      ? !backhaulGroup.some(route => {
        return selectedStops.every(stopId => {
          return route.stops.some(stop => stop.id === stopId);
        });
      })
      : false;

  // Callbacks
  const handleDownloadStops = () =>
    downloadStops(
      allStops.filter(stop => selectedStops.includes(stop.id)),
      "route",
      isPastProject
    );

  const handleDeleteStops = () => {
    deleteStops(selectedStops, true);
    //handleSelectAllClick();
  };

  const isFOJW = checkIsFOJW();

  // Determine for selected stops whether any already has pod photo enabled
  const selectedStopObjects = allStops.filter(stop => selectedStops.includes(stop.id));
  const hasAnyPodPhotoEnabledForSelectedStops = selectedStopObjects.some(
    stop => stop && stop.requirements && stop.requirements.picture
  );

  // Reopen: only completed/skipped stops in the selection can be reopened.
  const reopenableStopIds = selectedStopObjects
    .filter(stop => stop && (stop.status === "finished" || stop.status === "skipped"))
    .map(stop => stop.id);

  const handleReopenStops = () => {
    if (props.reopenStops) props.reopenStops(reopenableStopIds, showingRouteId);
  };

  return (
    <>
      <div className={classes.toolbarWrapper}>
        <SmallSearchBox
          isEnhancedSearch
          placeholder={
            published || locked
              ? "Search stops within route"
              : "Add or search stops within route"
          }
          showSearchField={true}
          className={classes.searchBox}
          searchText={searchInputValue}
          onSearchChanged={setSearchInputValue}
          dataCy="search-route-stops"
          setFocusedOuter={focused =>
            focused ? onSearchFocus() : onSearchBlur()
          }
        />
      </div>
      {!!numSelected && (
        <div className={classes.toolbarActions}>
          <div className={classes.checkboxWrapper}>
            <OptimizedTooltip
              title={allStopsSelected ? "Deselect all" : <>Select all</>}
              placement="bottom"
            >
              <Checkbox
                color="primary"
                indeterminate={!allStopsSelected}
                checked={allStopsSelected}
                onChange={handleSelectAllClick}
              />
            </OptimizedTooltip>
            <Typography className={classes.selectedText}>
              {numSelected} selected
            </Typography>
          </div>
          <Box display={"flex"} alignItems={"center"}>
            {!isDifferentStops && (
              <>
                <MoveToVehicle
                  routeGroups={routeGroups}
                  isUnassigned={isUnassigned}
                  routeId={showingRouteId}
                  editId={selectedStops}
                  setLoadingStop={setLoadingStop}
                  isIcon={true}
                />
                <Divider
                  flexItem
                  orientation="vertical"
                  style={{ margin: "0 4px" }}
                />
              </>
            )}
            <OptimizedTooltip
              placement="bottom"
              title={
                hasCompletedStopInSelection
                  ? `Reopen stop${makeTextPlural(reopenableStopIds.length)}`
                  : "Select completed or skipped stops to reopen"
              }
            >
              <span>
                <IconButton
                  className={classes.iconButton}
                  disabled={!hasCompletedStopInSelection}
                  onClick={() =>
                    !reopenConfirmation && setReopenConfirmation(true)
                  }
                >
                  <RestoreIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>
            <OptimizedTooltip placement="bottom" title="Download stops">
              <span>
                <IconButton
                  onClick={handleDownloadStops}
                  className={classes.iconButton}
                >
                  <SaveAltIcon />
                </IconButton>
              </span>
            </OptimizedTooltip>
            {!published && !locked && (
              <OptimizedTooltip
                placement="bottom"
                title={
                  hasCompletedStopInSelection
                    ? "Unable to delete completed stops."
                    : `Delete stop${makeTextPlural(selectedStops.length)}`
                }
              >
                <span>
                  <IconButton
                    className={classes.iconButton}
                    disabled={locked || hasCompletedStopInSelection}
                    onClick={e =>
                      !deleteConfirmation && setDeleteConfirmation(true)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </OptimizedTooltip>
            )}
            {isFOJW && (
              <>
                <Divider flexItem orientation="vertical" style={{ margin: "0 4px" }} />
                {hasAnyPodPhotoEnabledForSelectedStops ? (
                  <OptimizedTooltip placement="bottom" title="Disable POD photo for selected">
                    <span>
                      <IconButton
                        onClick={() =>
                          props.setPodPhotoRequirementForStops &&
                          props.setPodPhotoRequirementForStops(selectedStops, false)
                        }
                        className={classes.iconButton}
                      >
                        <NoPhotographyIcon />
                      </IconButton>
                    </span>
                  </OptimizedTooltip>
                ) : (
                  <OptimizedTooltip placement="bottom" title="Enable POD photo for selected">
                    <span>
                      <IconButton
                        onClick={() =>
                          props.setPodPhotoRequirementForStops &&
                          props.setPodPhotoRequirementForStops(selectedStops, true)
                        }
                        className={classes.iconButton}
                      >
                        <CameraIcon />
                      </IconButton>
                    </span>
                  </OptimizedTooltip>
                )}
              </>
            )}
          </Box>
        </div>
      )}
      <ConfirmDialog
        isProject
        open={deleteConfirmation}
        handleClose={() => setDeleteConfirmation(false)}
        action={handleDeleteStops}
        title={`Delete ${selectedStops.length === 1 ? "a" : ""
          } stop${makeTextPlural(selectedStops.length)}?`}
        body={`Are you sure you want to delete ${selectedStops.length > 1 ? selectedStops.length : "a"
          } stop${makeTextPlural(selectedStops.length)}?`}
      />
      <ConfirmDialog
        isProject
        open={reopenConfirmation}
        handleClose={() => setReopenConfirmation(false)}
        action={handleReopenStops}
        confirmText="Reopen"
        title={`Reopen ${reopenableStopIds.length} stop${makeTextPlural(
          reopenableStopIds.length
        )}?`}
        body={`This resets the selected completed/skipped stop${makeTextPlural(
          reopenableStopIds.length
        )} to to-do, clearing proof of delivery, photos and completion time, and recalculating route times. Customers are not notified.`}
      />
    </>
  );
}

const mapStateToProps = state => ({
  project: state.project,
  showingRouteId: state.project.showingRouteId,
  published: ["published", "expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  isPastProject: ["expired", "finished"].includes(
    state.project.status.toLowerCase()
  ),
  routeGroups: state.project.solution ? state.project.solution.routeGroups : [],
  isUnassigned:
    state.project.solution &&
    state.project.solution.unassigned.stops.findIndex(
      el => state.extendable.id === el.id
    ) > -1
});

export default connect(mapStateToProps, {
  deleteStops,
  reopenStops,
  setInfoDialog,
  setLoadingRoutesList,
  setPodPhotoRequirementForStops
})(RoutesToolbar);
